/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps } from '@wordpress/block-editor';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {WPElement} Element to render.
 */

import { Fragment } from '@wordpress/element';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, QueryControls, FormTokenField, SelectControl } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import ServerSideRender from '@wordpress/server-side-render';

export default function Edit( props ) {

	const termsQuery = { post_type: 'itineraries', per_page: 100 };

	const tripTypes = useSelect( ( select ) => {
		return select( 'core' ).getEntityRecords( 'taxonomy', 'itinerary_types', termsQuery );
	}, [] );

	const tripDestinations = useSelect( ( select ) => {
		return select( 'core' ).getEntityRecords( 'taxonomy', 'travel_locations', termsQuery );
	}, [] );

	const tripActivities = useSelect( ( select ) => {
		return select( 'core' ).getEntityRecords( 'taxonomy', 'activity', termsQuery );
	}, [] );

	const { setAttributes, attributes } = props;
	const { query, layoutType, filterType } = attributes;
	const {
		orderBy,
		order,
		selectedTripTypes,
		selectedTripDestinations,
		selectedTripActivities,
		numberOfItems,
	} = query;

	const TripTypeSuggestions = tripTypes?.reduce(
		(accumulator, category) => ({
			...accumulator,
			[category.name]: category,
		}),
		{}
	) ?? {};

	let selected_trip_types = [];
	if (selectedTripTypes && selectedTripTypes.length > 0) {
		selected_trip_types = selectedTripTypes.map((type) => {
			return type.name;
		})
	}

	const TripDestinationSuggestions = tripDestinations?.reduce(
		(accumulator, category) => ({
			...accumulator,
			[category.name]: category,
		}),
		{}
	) ?? {};
	let selected_trip_destinations = [];
	if (selectedTripDestinations && selectedTripDestinations.length > 0) {
		selected_trip_destinations = selectedTripDestinations.map((type) => {
			return type.name;
		})
	}

	const TripActivitySuggestions = tripActivities?.reduce(
		(accumulator, category) => ({
			...accumulator,
			[category.name]: category,
		}),
		{}
	) ?? {};
	let selected_trip_activities = [];
	if (selectedTripActivities && selectedTripActivities.length > 0) {
		selected_trip_activities = selectedTripActivities.map((type) => {
			return type.name;
		})
	}

	return (
		<div { ...useBlockProps() }>
			<Fragment>
				<InspectorControls>
					<PanelBody title={__('Filters', 'wp-travel-blocks')}>
							<SelectControl
								label={__('Select Layout', 'wp-travel-blocks')}
								value={ layoutType }
								options={[
									{
										label: __('Default Layout', 'wp-travel-blocks'),
										value:'default-layout'
									},{
										label: __('Layout One', 'wp-travel-blocks'),
										value:'layout-one'
									}, {
										label: __('Layout Two', 'wp-travel-blocks'),
										value:'layout-two'
									}, {
										label: __('Layout Three', 'wp-travel-blocks'),
										value:'layout-three'
									}, {
										label: __('Layout Four', 'wp-travel-blocks'),
										value:'layout-four'
									}
								]}
								onChange={ (val) => setAttributes( { layoutType: val } ) }
							/>
							<QueryControls
								maxItems={5}
								{...{ orderBy, order, numberOfItems }}
								onNumberOfItemsChange={(newNumberOfItems) =>
									setAttributes({
										query: {
											...query,
											numberOfItems: newNumberOfItems,
										},
									})
								}
							/>
							<SelectControl
								label={__('Filter Type', 'wp-travel-blocks')}
								value={ filterType }
								options={[
									{
										label: 'Trip Type',
										value: 'itinerary_types'
									},
									{
										label: 'Destination',
										value: 'travel_locations'
									},
									{
										label: 'Activity',
										value: 'activity'
									}
								]}
								onChange={ (value) => {
									setAttributes({ filterType: value })
								} }
							/>
							{
								filterType == "itinerary_types" &&
								<FormTokenField
									maxLength={5}
									label={__('Trip Type')}
									value={selected_trip_types ?? []}
									suggestions={Object.keys(TripTypeSuggestions).map(name => { return name })}
									onChange={ tokens => {
										const allCategories = tokens.map((token) => {
											const newData  = typeof token === 'string' ? TripTypeSuggestions[ token ] : token;
											const selected = {
												count: newData.count,
												id: newData.id,
												description: newData.description,
												link: newData.link,
												taxonomy: newData.taxonomy,
												name: newData.name,
												slug: newData.slug,
											}
											return selected;
										} );
										setAttributes({
											query: {
												...query,
												selectedTripTypes: allCategories,
											},
										} );
									} }
								/>
							}
							{
								filterType == "travel_locations" &&
								<FormTokenField
									maxLength={5}
									label={ __('Trip Destinations') }
									value={ selected_trip_destinations ?? [] }
									suggestions={Object.keys(TripDestinationSuggestions).map(name => { return name })}
									onChange={ tokens => {
										const allCategories = tokens.map((token) => {
											const newData  = typeof token === 'string' ? TripDestinationSuggestions[ token ] : token;
											const selected = {
												count: newData.count,
												id: newData.id,
												description: newData.description,
												link: newData.link,
												taxonomy: newData.taxonomy,
												name: newData.name,
												slug: newData.slug,
											}
											return selected;
										} );
										setAttributes({
											query: {
												...query,
												selectedTripDestinations: allCategories,
											},
										});
									}}
								/>
							}
							{
								filterType == "activity" &&
								<FormTokenField
									maxLength={5}
									label={ __('Trip Activities', 'wp-travel-blocks') }
									value={ selected_trip_activities ?? [] }
									suggestions={Object.keys(TripActivitySuggestions).map(name => { return name })}
									onChange={tokens => {
										const allCategories = tokens.map((token) => {
											const newData  = typeof token === 'string' ? TripActivitySuggestions[ token ] : token;
											const selected = {
												count: newData.count,
												id: newData.id,
												description: newData.description,
												link: newData.link,
												taxonomy: newData.taxonomy,
												name: newData.name,
												slug: newData.slug,
											}
											return selected;
										} );
										setAttributes({
											query: {
												...query,
												selectedTripActivities: allCategories,
											},
										});
									}}
								/>
							}
					</PanelBody>
				</InspectorControls>
			</Fragment>
			<ServerSideRender
				block="wp-travel-blocks/filterable-trips"
				attributes={ props.attributes }
			/>
			
		</div>
	);
}
